import React, { ReactElement, useState } from 'react'
import styled, { CSSProperties } from 'styled-components'
import { ISelectOption } from '../../../fields/SelectField'
import colors from '../../../lib/colors'
import { ZIndex } from '../../../lib/theme'
import { IColorTheme } from '../../../types/itemTypes'

const Styles = styled.div`
  position: relative;
`
const Select = styled.div`
  background-color: ${colors.white};
  border: 1px solid ${colors.darkestGray};
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 10px;
  box-sizing: border-box;
  &.open {
    background-color: ${(props): string => props.theme.colourHighlight};
    color: ${(props): string => props.theme.colourTextOnHighlight};
  }
`
const Arrow = styled.div`
  border-left: 7px solid transparent;
  border-right: 7px solid transparent;
  border-top: 7px solid ${colors.black};
  margin: 3px 0 0 10px;
`
const Dropdown = styled.div`
  position: absolute;
  z-index: ${ZIndex.AboveContent};
  width: 100%;
`
const OptionsContainer = styled.div`
  background-color: ${colors.white};
  border: 1px solid ${colors.darkestGray};
  box-sizing: border-box;
  padding: 10px;
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  & div {
    margin-top: 10px;
    &:first-child {
      margin-top: 0;
    }
  }
`
const Option = styled.div`
  cursor: pointer;
  min-height: 34px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  padding: 6px 10px;
  background-color: ${colors.lightGray};
  line-height: 17px;
  :hover {
    background-color: ${(props): string => props.theme.colourHighlight};
    color: ${(props): string => props.theme.colourTextOnHighlight};
  }
`
interface IItemGridSelectProps<T = string> {
  selected: ISelectOption<T> | undefined
  options: ISelectOption<T>[]
  onSelect: (option?: ISelectOption<T>) => void
  colorTheme?: Partial<IColorTheme>
  placeholder?: string
  style?: CSSProperties
}
export default function ItemGridSelect<T>(
  props: IItemGridSelectProps<T>,
): ReactElement {
  const {
    selected, options, onSelect, colorTheme, placeholder, style,
  } = props
  const [open, setOpen] = useState(false)
  const placeholderText = placeholder || 'Select...'

  const renderOptions = options.map((option, index) => {
    const key = `${index}`
    return (
      <Option
        key={key}
        onClick={(): void => {
          onSelect(option)
          setOpen(false)
        }}
        theme={colorTheme}
      >
        {option.label}
      </Option>
    )
  })

  const placeholderOption = (
    <Option
      key="placeholder"
      onClick={(): void => {
        onSelect()
        setOpen(false)
      }}
      theme={colorTheme}
    >
      {placeholder || 'Select...'}
    </Option>
  )

  renderOptions.unshift(placeholderOption)

  return (
    <Styles
      onMouseEnter={(): void => setOpen(true)}
      onMouseLeave={(): void => setOpen(false)}
      style={style}
    >
      <Select
        onClick={(): void => setOpen(true)}
        className={open ? 'open' : ''}
        theme={colorTheme}
      >
        {selected ? selected.label : placeholderText}
        <Arrow />
      </Select>
      {open && (
        <Dropdown>
          <OptionsContainer>{renderOptions}</OptionsContainer>
        </Dropdown>
      )}
    </Styles>
  )
}
